// Master Apps · favoritos POR USUÁRIO.
//
// Contrato da API:
//   PUT    /api/me/favorites/:applicationId → 200 { data: { favorite: true } }
//   DELETE /api/me/favorites/:applicationId → 200 { data: { favorite: false } }
// Ambos idempotentes; 404 NOT_FOUND quando a aplicação não existe ou o usuário
// não pode vê-la. O campo `favorite` que vem em GET /api/applications e
// GET /api/applications/:slug é o do usuário logado.
import type { QueryKey } from '@tanstack/react-query';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { type ApiError, readApiError } from '@/hooks/use-applications';
import type { App } from '@/lib/catalog-config';
import { retryDelay, retryIdempotentMutation } from '@/lib/query-client';
import { getClerk } from '@/store/session';

// Mesma origem do `apiClient` (vazio = same-origin).
const baseUrl = import.meta.env.VITE_API_URL || '';

export interface ToggleFavoriteInput {
  /** `:applicationId` da rota (o `id` da aplicação, não o slug). */
  id: string;
  /** Estado DESEJADO depois do clique: true favorita (PUT), false remove (DELETE). */
  favorite: boolean;
}

/**
 * Chamada por `fetch` relativo em vez do client RPC: o `apiClient` (hono/client)
 * só monta caminhos que existam no tipo `AppType` da API, e
 * `api.me.favorites[':applicationId']` ainda não está lá (a rota está sendo
 * adicionada em paralelo) — o RPC nem compilaria. Para não perder nada do
 * `apiClient`, este fetch repete exatamente o que ele faz: Bearer da sessão do
 * Clerk (ausente em dev, e aí a API trata como dev-user), `credentials:
 * 'include'` e `cache: 'no-store'`. Quando o tipo expuser o caminho, trocar por
 * `apiClient.api.me.favorites[':applicationId'].$put({ param: { applicationId: id } })`.
 */
async function requestFavorite({ id, favorite }: ToggleFavoriteInput): Promise<boolean> {
  const headers = new Headers();
  try {
    const token = await getClerk()?.session?.getToken();
    if (token) headers.set('Authorization', `Bearer ${token}`);
  } catch {
    // sem token (não logado ou Clerk indisponível) — segue sem header
  }

  const res = await fetch(`${baseUrl}/api/me/favorites/${encodeURIComponent(id)}`, {
    method: favorite ? 'PUT' : 'DELETE',
    headers,
    credentials: 'include',
    cache: 'no-store',
  });

  if (!res.ok) {
    const err = await readApiError(
      res,
      favorite ? 'Falha ao favoritar a aplicação' : 'Falha ao remover dos favoritos',
    );
    // 429 sem `retryAfterSec` no envelope: aproveita o header Retry-After (em
    // segundos) para o retryDelay saber quanto esperar.
    if (err.status === 429 && err.details?.retryAfterSec == null) {
      const fromHeader = Number(res.headers.get('Retry-After'));
      if (Number.isFinite(fromHeader) && fromHeader > 0) {
        err.details = { ...err.details, retryAfterSec: fromHeader };
      }
    }
    throw err;
  }

  const json = (await res.json().catch(() => null)) as { data?: { favorite?: boolean } } | null;
  // Vale o que o servidor diz; o estado pedido é só o fallback (204/corpo vazio).
  return typeof json?.data?.favorite === 'boolean' ? json.data.favorite : favorite;
}

/** Snapshot para rollback: a lista e cada detalhe já em cache. */
interface ToggleFavoriteContext {
  list: App[] | undefined;
  details: Array<[QueryKey, App | undefined]>;
}

export function useToggleFavorite() {
  const qc = useQueryClient();

  /** Escreve `favorite` na lista `['applications']` e em cada `['application', slug]`. */
  const patch = (id: string, favorite: boolean) => {
    qc.setQueryData<App[]>(['applications'], (old) =>
      old?.map((a) => (a.id === id ? { ...a, favorite } : a)),
    );
    qc.setQueriesData<App>({ queryKey: ['application'] }, (old) =>
      old?.id === id ? { ...old, favorite } : old,
    );
  };

  return useMutation<boolean, ApiError, ToggleFavoriteInput, ToggleFavoriteContext>({
    mutationFn: requestFavorite,
    // PUT/DELETE de favorito são idempotentes: repetir chega no mesmo estado.
    // Só repetimos em 429, respeitando o tempo pedido pela API.
    retry: retryIdempotentMutation,
    retryDelay,

    // Optimistic update: a estrela vira no clique, sem esperar a rede.
    onMutate: async ({ id, favorite }) => {
      // Cancela refetches em voo para eles não sobrescreverem o valor otimista.
      await qc.cancelQueries({ queryKey: ['applications'] });
      await qc.cancelQueries({ queryKey: ['application'] });
      const context: ToggleFavoriteContext = {
        list: qc.getQueryData<App[]>(['applications']),
        details: qc.getQueriesData<App>({ queryKey: ['application'] }),
      };
      patch(id, favorite);
      return context;
    },

    // Resposta do servidor manda (ex.: DELETE em algo que já não era favorito).
    onSuccess: (favorite, { id }) => patch(id, favorite),

    // Falhou: desfaz o optimistic update e avisa, para a estrela nunca mentir.
    onError: (error, { favorite }, context) => {
      if (context) {
        qc.setQueryData(['applications'], context.list);
        for (const [key, data] of context.details) qc.setQueryData(key, data);
      }
      toast.error(
        error.message ||
          (favorite ? 'Não foi possível favoritar' : 'Não foi possível remover dos favoritos'),
      );
    },

    // Sempre: revalida com o servidor (a lista e o detalhe aberto).
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: ['applications'] });
      void qc.invalidateQueries({ queryKey: ['application'] });
    },
  });
}
